import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useContactInfo } from '@/hooks/useContactInfo';
import { AnalyticsService } from "@/services/analyticsService";
import { useProviderContact } from "@/hooks/useProviderContact";
import { useAreaDisplay } from "@/hooks/useAreaDisplay";
import { 
  ServiceProfileHeader,
  ServiceProfileMetaBadges,
  ServiceProfileAreas,
  ServiceProfileWebsite,
  ServiceProfileContactActions,
  ServiceProfileErrorBoundary
} from './service-profile';

interface ServiceProfileProps {
  id: string;
  name: string;
  image: string;
  categories: string[];
  description: string;
  suburbs: string[];
  rating: number;
  reviewCount: number;
  user_id: string; // Add user_id to fetch profile data
  isVerified: boolean;
  isFeatured?: boolean; // Add support for featured badge
  isTopRated?: boolean; // Keep this prop for backward compatibility
  joinedDate?: string;
  websiteUrl?: string; // Add support for website URL
  subscriptionTier?: string;
  whatsapp_number?: string | null; // Listing-level WhatsApp
}

export default function ServiceProfile({
  id,
  name,
  image,
  categories,
  description,
  suburbs,
  rating,
  reviewCount,
  user_id,
  isVerified,
  isFeatured = false, // Default to false
  isTopRated: propIsTopRated,
  joinedDate,
  websiteUrl,
  subscriptionTier,
  whatsapp_number: listingWhatsApp,
}: ServiceProfileProps) {
  const { t } = useTranslation('common');
  const { contactInfo: userContactInfo, loading } = useContactInfo({
    userId: user_id,
    listingWhatsApp,
  });
  const { trackContact } = useProviderContact(id);
  
  // Track provider profile view when component mounts
  useEffect(() => {
    AnalyticsService.trackProviderView(id, document.referrer);
  }, [id]);
  
  // Calculate if top rated based on rating (>= 4.5) or explicit database flag
  const isTopRated = propIsTopRated || rating >= 4.5;
  
  // (legacy contact resolution removed in favor of useContactInfo)
  // Derive helper lists & contact values
  const categoryList = Array.isArray(categories) ? categories : [];
  const suburbList = Array.isArray(suburbs) ? suburbs : [];
  const { areaLabels, areaSummaryLabel } = useAreaDisplay(suburbList);
  const whatsappNumber = userContactInfo?.whatsapp_number || "";
  const whatsappMessage = encodeURIComponent(
    t('contact.providerWhatsappInquiryMessage', { serviceName: name })
  );
  const whatsappLink = whatsappNumber ? `https://wa.me/${whatsappNumber.replace(/[^\d]/g, '')}?text=${whatsappMessage}` : "";
  const whatsappVerified = isVerified;
  const displayRating = rating > 0 ? rating.toFixed(1) : "-";
  
  if (loading) {
    return (
      <article className="overflow-hidden rounded-md border border-brand-cloud bg-white">
        <div className="p-5 text-center">Loading provider information...</div>
      </article>
    );
  }
  
  return (
    <ServiceProfileErrorBoundary>
      <article className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm" data-test-responsive="service-profile">
        <ServiceProfileHeader
          id={id}
          name={name}
          image={image}
          areaSummaryLabel={areaSummaryLabel}
          whatsappVerified={whatsappVerified}
          isTopRated={isTopRated}
          isFeatured={isFeatured}
        />
        <div className="space-y-7 p-5 md:p-8">
          <ServiceProfileMetaBadges
            displayRating={displayRating}
            reviewCount={reviewCount}
            categoryList={categoryList}
            joinedDate={joinedDate}
          />

          <section className="grid gap-4 md:grid-cols-[minmax(0,0.28fr)_minmax(0,0.72fr)] md:gap-8">
            <h2 className="public-card-title">About this service</h2>
            <p className="whitespace-pre-line leading-7 text-brand-slate">{description}</p>
          </section>

          <ServiceProfileAreas areaLabels={areaLabels} />
          <ServiceProfileWebsite
            websiteUrl={websiteUrl}
            subscriptionTier={subscriptionTier}
            onClickWebsite={() => trackContact('website')}
          />
          <ServiceProfileContactActions
            whatsappNumber={whatsappNumber}
            whatsappLink={whatsappLink}
            onClickWhatsApp={() => trackContact('whatsapp')}
          />
        </div>
      </article>
    </ServiceProfileErrorBoundary>
  );
}
